import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument } from '../firebase';
import AssignmentSection from './AssignmentSection';
import LoadingChip from './LoadingChip';
import { Home, BedDouble, Car, Wifi, MapPin, ExternalLink, Key, Copy, Check } from 'lucide-react';

const STAY_INFO_DOC_ID = 'stayInfo';

const DEFAULT_STAY_INFO = {
  address: 'Kite Beach Villa, Las Picuas, Río Grande, Puerto Rico',
  airbnbUrl: 'https://www.airbnb.com/rooms/705974426375983126?adults=8&check_in=2027-01-07&check_out=2027-01-14&children=0&infants=0&pets=0&wishlist_item_id=11006482200567&source_impression_id=p3_1785081729_P3w9wrW89RHaZJCF&previous_page_section_name=1001',
  wifiNetwork: 'TBD',
  wifiPassword: 'TBD',
};

function AccessWifiPanel() {
  const [info, setInfo] = useState(DEFAULT_STAY_INFO);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState(DEFAULT_STAY_INFO);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToCollection('settings', (data) => {
      const doc = (data || []).find(d => d.id === STAY_INFO_DOC_ID);
      if (doc) {
        setInfo({
          address: doc.address || 'TBD',
          airbnbUrl: doc.airbnbUrl || '',
          wifiNetwork: doc.wifiNetwork || 'TBD',
          wifiPassword: doc.wifiPassword || 'TBD',
        });
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleStartEdit = () => {
    setEditValues(info);
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const cleaned = {
      address: editValues.address.trim() || 'TBD',
      airbnbUrl: editValues.airbnbUrl.trim(),
      wifiNetwork: editValues.wifiNetwork.trim() || 'TBD',
      wifiPassword: editValues.wifiPassword.trim() || 'TBD',
    };
    await saveDocument('settings', STAY_INFO_DOC_ID, { id: STAY_INFO_DOC_ID, ...cleaned });
    setIsEditing(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(info.wifiPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <div className="glass-panel access-wifi-panel" style={{ padding: '24px', marginBottom: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'var(--font-title)', fontSize: '1.4rem', margin: 0 }}>
          <Wifi size={22} strokeWidth={1.75} />
          Access &amp; WiFi
        </h3>
        <button
          className={isEditing ? 'btn-secondary' : 'btn-primary'}
          style={{ fontSize: '0.85rem', padding: '8px 18px', borderRadius: '0px' }}
          onClick={() => (isEditing ? setIsEditing(false) : handleStartEdit())}
        >
          {isEditing ? '✕ Cancel' : '[Edit]'}
        </button>
      </div>

      {isLoading ? (
        <LoadingChip text="Loading stay info..." />
      ) : isEditing ? (
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                className="input-field"
                placeholder="TBD"
                value={editValues.address}
                onChange={e => setEditValues(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Airbnb Listing URL</label>
              <input
                type="url"
                className="input-field"
                placeholder="https://www.airbnb.com/rooms/..."
                value={editValues.airbnbUrl}
                onChange={e => setEditValues(prev => ({ ...prev, airbnbUrl: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>WiFi Network</label>
              <input
                type="text"
                className="input-field"
                placeholder="TBD"
                value={editValues.wifiNetwork}
                onChange={e => setEditValues(prev => ({ ...prev, wifiNetwork: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>WiFi Password</label>
              <input
                type="text"
                className="input-field"
                placeholder="TBD"
                value={editValues.wifiPassword}
                onChange={e => setEditValues(prev => ({ ...prev, wifiPassword: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn-primary" style={{ fontSize: '0.85rem', padding: '8px 20px' }}>Save</button>
            <button type="button" className="btn-secondary" style={{ fontSize: '0.85rem', padding: '8px 16px' }} onClick={() => setIsEditing(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="access-wifi-grid">
          <div className="access-wifi-item">
            <span className="access-wifi-label"><MapPin size={14} strokeWidth={2} /> Address</span>
            <span className="access-wifi-value">{info.address}</span>
          </div>
          <div className="access-wifi-item">
            <span className="access-wifi-label"><ExternalLink size={14} strokeWidth={2} /> Airbnb Listing</span>
            {info.airbnbUrl ? (
              <a href={info.airbnbUrl} target="_blank" rel="noopener noreferrer" className="access-wifi-link">
                View Listing →
              </a>
            ) : (
              <span className="access-wifi-value muted">TBD</span>
            )}
          </div>
          <div className="access-wifi-item">
            <span className="access-wifi-label"><Wifi size={14} strokeWidth={2} /> WiFi Network</span>
            <span className="access-wifi-value">{info.wifiNetwork}</span>
          </div>
          <div className="access-wifi-item">
            <span className="access-wifi-label"><Key size={14} strokeWidth={2} /> WiFi Password</span>
            <span className="access-wifi-value-row">
              <span className="access-wifi-value">{info.wifiPassword}</span>
              <button type="button" className="btn-copy-chip" onClick={handleCopyPassword} title="Copy password">
                {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Keep the den bedroom pinned to the end of the room list, matching the
// original Room Assignments ordering.
const roomSortFn = (a, b) => {
  if (a.id === 'den') return 1;
  if (b.id === 'den') return -1;
  return a.id.localeCompare(b.id);
};

const carSortFn = (a, b) => (a.name || '').localeCompare(b.name || '');

export default function Stay({ users = [] }) {
  return (
    <div className="tab-panel">
      <div className="view-title-container" style={{ marginBottom: '16px' }}>
        <div className="view-title">
          <h2>
            <Home className="subpage-header-icon" strokeWidth={1.75} />
            Lodging
          </h2>
        </div>
      </div>

      <AccessWifiPanel />

      <AssignmentSection
        icon={BedDouble}
        title="Room Assignments"
        collectionName="rooms"
        idPrefix="room"
        users={users}
        sortFn={roomSortFn}
        metaFields={[
          { key: 'beds', label: 'Beds', placeholder: 'e.g. 1 Queen, 1 Twin' },
        ]}
        assignmentField="occupant"
        assignmentMode="csv"
        assignmentLabel="Guests"
        addAssignmentPlaceholder="-- Select --"
        emptyAssignmentPlaceholder="Vacant"
        addButtonLabel="+ Add Room"
        nameLabel="Room Name"
        namePlaceholder="e.g. Loft Bedroom"
        loadingText="Syncing Room Layouts..."
        emptyStateText="No rooms found."
      />

      <AssignmentSection
        icon={Car}
        title="Car Assignments"
        collectionName="cars"
        idPrefix="car"
        users={users}
        sortFn={carSortFn}
        metaFields={[
          { key: 'description', label: 'Notes', placeholder: 'e.g. has roof rack', flex: 2, width: '160px' },
          { key: 'capacity', label: 'Seats', placeholder: '5', type: 'number', width: '80px', badge: true, badgePrefix: 'Seats ' },
        ]}
        assignmentField="drivers"
        assignmentMode="array"
        assignmentLabel="Drivers"
        addAssignmentPlaceholder="-- Add driver --"
        emptyAssignmentPlaceholder="No drivers assigned"
        addButtonLabel="+ Add Car"
        nameLabel="Car Name / Nickname"
        namePlaceholder="e.g. Millennium Falcon"
        loadingText="Loading Cars..."
        emptyStateText="No cars added yet."
      />
    </div>
  );
}
